import { SignIn, useAuth } from "@clerk/clerk-react";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
  AUTHORIZE_SHARED_REPORT_ACCESS_MUTATION,
  SHARED_REPORT_SHARE_CHALLENGE_MUTATION,
  SHARED_VIEW_SHELL_QUERY,
} from "@/constants/queries";
import { ShareSessionProvider } from "@/app/share/report/ShareSessionContext";
import { SharedReportView } from "@/app/share/report/SharedReportView";
import type { SharedViewLayout } from "@/app/share/report/sharedReportTypes";
import { normalizeSharedViewLayout } from "@/app/share/report/sharedReportToDefinition";
import { FullPageLoading } from "@/components/atoms/FullPageLoading";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { formatApolloError } from "@/utils/graphqlErrors";
import { Typography } from "@/components/ui/typography";

export default function SharedReportPage() {
  const { token } = useParams<{ token: string }>();
  const { isLoaded, isSignedIn } = useAuth();
  const client = useApolloClient();
  const [layout, setLayout] = useState<SharedViewLayout | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const [reportIdentifier, setReportIdentifier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accessLoading, setAccessLoading] = useState(false);

  useEffect(() => {
    sessionTokenRef.current = sessionToken;
  }, [sessionToken]);

  const [
    loadChallenge,
    {
      data: challengeData,
      loading: challengeLoading,
      error: challengeMutationError,
    },
  ] = useMutation(SHARED_REPORT_SHARE_CHALLENGE_MUTATION);

  useEffect(() => {
    if (!token) return;
    void loadChallenge({ variables: { token } });
  }, [token, loadChallenge]);

  const [authorizeAccess] = useMutation(
    AUTHORIZE_SHARED_REPORT_ACCESS_MUTATION,
  );

  const layoutName = challengeData?.sharedReportShareChallenge.layoutName ?? "";
  const challengeError = challengeMutationError
    ? formatApolloError(challengeMutationError)
    : !token
      ? "Invalid share link."
      : null;

  useEffect(() => {
    const nextIdentifier =
      challengeData?.sharedReportShareChallenge.reportIdentifier ?? null;
    if (nextIdentifier) {
      setReportIdentifier(nextIdentifier);
    }
  }, [challengeData]);

  const loadSharedView = useCallback(
    async (options?: { reuseSession?: boolean }) => {
      if (!token || !reportIdentifier) return;
      setAccessLoading(true);
      setError(null);
      try {
        let nextSessionToken = options?.reuseSession
          ? sessionTokenRef.current
          : null;
        if (!nextSessionToken) {
          const { data: authData } = await authorizeAccess({
            variables: { token },
          });
          nextSessionToken =
            authData?.authorizeSharedReportAccess.sessionToken ?? null;
          if (!nextSessionToken) {
            throw new Error("Unable to authorize access to this dashboard.");
          }
          setSessionToken(nextSessionToken);
        }

        const { data: layoutData } = await client.query({
          query: SHARED_VIEW_SHELL_QUERY,
          variables: {
            id: identifierAndTypenameToId(
              reportIdentifier,
              "SharedReportObject",
            ),
          },
          fetchPolicy: "no-cache",
          context: {
            headers: {
              Authorization: `ShareSession ${nextSessionToken}`,
            },
          },
        });
        setLayout(normalizeSharedViewLayout(layoutData.node));
      } catch (e) {
        setLayout(null);
        setSessionToken(null);
        setError(formatApolloError(e) || "Unable to access this dashboard");
      } finally {
        setAccessLoading(false);
      }
    },
    [authorizeAccess, client, reportIdentifier, token],
  );

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !token || !reportIdentifier) return;
    void loadSharedView();
  }, [isLoaded, isSignedIn, token, reportIdentifier, loadSharedView]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !token || !sessionToken) return;

    const refreshShell = () => {
      if (document.visibilityState !== "visible") return;
      void loadSharedView({ reuseSession: true });
    };

    window.addEventListener("focus", refreshShell);
    document.addEventListener("visibilitychange", refreshShell);
    return () => {
      window.removeEventListener("focus", refreshShell);
      document.removeEventListener("visibilitychange", refreshShell);
    };
  }, [isLoaded, isSignedIn, loadSharedView, sessionToken, token]);

  const shareUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `/share/report/${token}`;

  const displayError = error ?? challengeError;

  const showSignIn =
    isLoaded && !isSignedIn && !challengeLoading && !layout && !displayError;

  const isPageLoading =
    !layout &&
    !displayError &&
    !showSignIn &&
    (!isLoaded ||
      challengeLoading ||
      (isSignedIn && (accessLoading || !sessionToken)));

  return (
    <div className="h-screen overflow-hidden w-full flex flex-col">
      {layout && sessionToken ? (
        <ShareSessionProvider sessionToken={sessionToken}>
          <SharedReportView
            layout={layout}
            title={layoutName || layout.name || "Shared report"}
          />
        </ShareSessionProvider>
      ) : isPageLoading ? (
        <FullPageLoading />
      ) : displayError ? (
        <div className="flex-1 flex items-center justify-center pl-4 pr-4">
          <Typography level="body-sm" className="text-destructive text-center">
            {displayError}
          </Typography>
        </div>
      ) : showSignIn ? (
        <div className="flex-1 flex flex-col gap-4 items-center justify-center pl-4 pr-4">
          <Typography level="h3" className="text-center">
            {layoutName || "Shared report"}
          </Typography>
          <Typography level="body-sm" className="text-center">
            Sign in to view this dashboard.
          </Typography>
          <SignIn
            routing="hash"
            forceRedirectUrl={shareUrl}
            fallbackRedirectUrl={shareUrl}
          />
        </div>
      ) : null}
    </div>
  );
}
